/**
 * React Starter Kit (https://www.reactstarterkit.com/)
 *
 * Copyright © 2014-present Kriasoft, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */

import 'whatwg-fetch';
import React from 'react';
import ReactDOM from 'react-dom';
import { IntlProvider } from 'react-intl';
import deepForceUpdate from 'react-deep-force-update';
import queryString from 'query-string';
import { createPath } from 'history/PathUtils';
import App from './components/App';
import createFetch from './createFetch';
import configureStore from './store/configureStore';
import history from './history';
import { updateMeta } from './DOMUtils';
import router from './router';
import UaParser from 'ua-parser-js';
import { handleTracker } from './lib/videoTracker';
import tracker from './lib/tracker';
import _get from 'lodash/get';
import dateFormat from 'dateformat';

// Global (context) variables that can be easily accessed from any React component
// https://facebook.github.io/react/docs/context.html
const context = {
  // Enables critical path CSS rendering
  // https://github.com/kriasoft/isomorphic-style-loader
  insertCss: (...styles) => {
    // eslint-disable-next-line no-underscore-dangle
    const removeCss = styles.map(x => x._insertCss());
    return () => {
      removeCss.forEach(f => f());
    };
  },
  // Universal HTTP client
  fetch: createFetch(fetch, {
    baseUrl: window.App.apiUrl
  }),
  // Initialize a new Redux store
  // http://redux.js.org/docs/basics/UsageWithReact.html
  store: configureStore(window.App.state, { history }),
  storeSubscription: null,
  isMobile: window.App.isMobile
};

const container = document.getElementById('app');
let currentLocation = history.location;
let appInstance;

const scrollPositionsHistory = {};

// Re-render the app when window.location changes
async function onLocationChange(location, action) {
  // Remember the latest scroll position for the previous location
  scrollPositionsHistory[currentLocation.key] = {
    scrollX: window.pageXOffset,
    scrollY: window.pageYOffset
  };
  // Delete stored scroll position for next page if any
  if (action === 'PUSH') {
    delete scrollPositionsHistory[location.key];
  }
  currentLocation = location;

  const isInitialRender = !action;
  try {
    context.pathname = location.pathname;
    context.query = queryString.parse(location.search);

    // Traverses the list of routes in the order they are defined until
    // it finds the first route that matches provided URL path string
    // and whose action method returns anything other than `undefined`.
    const route = await router.resolve(context);

    // Prevent multiple page renders during the routing process
    if (currentLocation.key !== location.key) {
      return;
    }

    if (route.redirect) {
      history.replace(route.redirect);
      return;
    }

    const renderReactApp = isInitialRender ? ReactDOM.hydrate : ReactDOM.render;

    const language = context.query.lang;

    appInstance = renderReactApp(
      <IntlProvider locale={language}>
        <App context={context}>{route.component}</App>
      </IntlProvider>,
      container,
      () => {
        if (isInitialRender) {
          // Switch off the native scroll restoration behavior and handle it manually
          // https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration
          if (window.history && 'scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
          }

          const elem = document.getElementById('css');
          if (elem) elem.parentNode.removeChild(elem);
          return;
        }

        document.title = route.title;

        updateMeta('description', route.description);
        // Update necessary tags in <head> at runtime here, ie:
        // updateMeta('keywords', route.keywords);
        // updateCustomMeta('og:url', route.canonicalUrl);
        // updateCustomMeta('og:image', route.imageUrl);
        // updateLink('canonical', route.canonicalUrl);
        // etc.

        let scrollX = 0;
        let scrollY = 0;
        const pos = scrollPositionsHistory[location.key];
        if (pos) {
          scrollX = pos.scrollX;
          scrollY = pos.scrollY;
        } else {
          const targetHash = location.hash.substr(1);
          if (targetHash) {
            const target = document.getElementById(targetHash);
            if (target) {
              scrollY = window.pageYOffset + target.getBoundingClientRect().top;
            }
          }
        }

        // Restore the scroll position if it was saved into the state
        // or scroll to the given #hash anchor
        // or scroll to top of the page
        window.scrollTo(scrollX, scrollY);

        // Google Analytics tracking. Don't send 'pageview' event after
        // the initial rendering, as it was already sent
        if (window.ga) {
          window.ga('send', 'pageview', createPath(location));
        }
      }
    );
  } catch (error) {
    if (__DEV__) {
      throw error;
    }

    console.error(error);

    // Do a full page reload if error occurs during client-side navigation
    if (!isInitialRender && currentLocation.key === location.key) {
      console.error('RSK will reload your page after error');
      window.location.reload();
    }
  }

  // if (context.store.getState().user.sid) {
  //   const tokenExpired = context.store.getState().user.tokenExpired * 1000;
  //   if (tokenExpired < Date.now()) {
  //     window.location.href = '/login';
  //   }
  // }

  track(context.store);
}

const track = async store => {
  // Parse Current URL
  const { search, pathname } = location;
  const urlParams = queryString.parse(search);

  // Get & Parse UA
  const UA = new UaParser();
  UA.setUA(navigator.userAgent);

  // Try get user_id & subs
  const user = store.getState().user;
  let adjustedSubs = [];
  // if (userId !== null || userId !== undefined) {
  //   adjustedSubs = await users.subscriptions.map(
  //     subs => `${_get(subs, 'subscriptionId', null)}`,
  //   );
  // } else {
  //   adjustedSubs = [null];
  // }

  // Try get platform and browser
  const platform = _get(UA.getDevice(), 'type', null);
  const osName = _get(UA.getOS(), 'name', null);
  const osVersion = _get(UA.getOS(), 'version', null);
  const os =
    osName !== null && osVersion !== null ? `${osName} ${osVersion}` : null;
  const vendor = _get(UA.getDevice(), 'vendor', null);
  const device = vendor !== null ? `${vendor}` : null;
  const browserName = _get(UA.getBrowser(), 'name', null);
  const browserVersion = _get(UA.getBrowser(), 'version', null);
  const browser =
    browserName !== null && browserVersion !== null
      ? `${browserName} ${browserVersion}`
      : null;

  // Initialize Payload
  const payload = {
    data: {
      project_id: 'supersoccertv',
      referrer: `${window.location.host}${currentLocation.pathname}${
        currentLocation.search
      }`,
      host: `${window.location.host}`,
      path: `${window.location.host}${location.pathname}${location.search}`,
      session_id: tracker.sessionId(window), // Try get+set session_id
      // page_content: document.title || null,
      ip: user.clientIp,
      platform,
      os,
      device,
      app: browser,
      client: 'sstv',
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      current_subscription_id: adjustedSubs,
      hit_timestamp: dateFormat(new Date(), 'yyyy-mm-dd hh:MM:ss'),
      utm_source: urlParams.utm_source || undefined,
      utm_medium: urlParams.utm_medium || undefined,
      utm_campaign: urlParams.utm_campaign || undefined
    },
    table: 'event_pages'
  };

  if (user.uid) {
    payload.user_id = user.uid;
  }
  // Check if the pathname contains the word "search" === current page is the search page
  const inSearchPage = ['search'].includes(currentLocation.pathname);

  // get the token
  const token = await tracker.getOrCreateToken();
  // Post to ds-feeder if there's token && not in search page
  if (token && !inSearchPage) tracker.sendPubSub(payload, token);

  const payloadVideo = {
    clientIp: user.clientIp,
    sessionId: tracker.sessionId(window),
    window: window,
    event: 'event_videos'
  };

  if (user.uid) {
    payloadVideo.userId = user.uid;
  }

  var videoData = {};

  if (location.pathname.includes('channels')) {
    videoData = {
      id: 'sstv'
    };
    handleTracker(payloadVideo, videoData);
  } else if (urlParams.v) {
    const videoId = urlParams.v;
    videoData = {
      id: videoId ? videoId : null
    };
    handleTracker(payloadVideo, videoData);
  }
};
// Handle client-side navigation by using HTML5 History API
// For more information visit https://github.com/mjackson/history#readme
history.listen(onLocationChange);
onLocationChange(currentLocation);

// Enable Hot Module Replacement (HMR)
if (module.hot) {
  module.hot.accept('./router', () => {
    if (appInstance && appInstance.updater.isMounted(appInstance)) {
      // Force-update the whole tree, including components that refuse to update
      deepForceUpdate(appInstance);
    }

    onLocationChange(currentLocation);
  });
}
